import React, { useEffect, useMemo, useState } from 'react'
import { FileText, TrendingUp, Loader2, Calendar } from "lucide-react"
import { CartesianGrid, LabelList, Line, LineChart, XAxis, YAxis, ResponsiveContainer } from "recharts"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart"
import { Button } from '../../components/ui/button'
import axios from 'axios'
import { format } from 'date-fns'
import { toast, Toaster } from 'sonner'
import { DateRange } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { enUS } from 'date-fns/locale'

const chartConfig = {
  users: {
    label: "Registered Users",
    color: "#0a67f2",
  },
}

const RegisterReport = () => {
  const today = new Date()
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

  const [dateRange, setDateRange] = useState([
    {
      startDate: firstDayOfMonth,
      endDate: today,
      key: 'selection',
    },
  ])

  const [showCalendar, setShowCalendar] = useState(false)
  const [userData, setUserData] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchUsers = async () => {
      const token = localStorage.getItem("adminToken")
      if (!token) return

      try {
        setIsLoading(true)
        const res = await axios.get("https://wavetechservices.in/api/admin/users", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        setUserData(res.data.data)
      } catch (err) {
        console.error("Error fetching users:", err)
        toast.error("Failed to fetch users")
      } finally {
        setIsLoading(false)
      }
    }

    fetchUsers()
  }, [])

  // Group by date
  const allGroupedData = useMemo(() => {
    const dateCounts = {}

    userData.forEach(user => {
      const date = user.createdAt.split("T")[0] // yyyy-mm-dd
      dateCounts[date] = (dateCounts[date] || 0) + 1
    })

    // Convert to array sorted by date
    const result = Object.entries(dateCounts).map(([date, count]) => ({
      date,
      users: count,
    }))

    result.sort((a, b) => new Date(a.date) - new Date(b.date))

    return result
  }, [userData])

  // Filter based on selected range
  const filteredData = useMemo(() => {
    if (!allGroupedData.length) return []

    const start = dateRange[0].startDate
    const end = dateRange[0].endDate

    return allGroupedData.filter((item) => {
      const itemDate = new Date(item.date)
      return itemDate >= start && itemDate <= end
    })
  }, [dateRange, allGroupedData])

  const percentageChange = useMemo(() => {
    if (filteredData.length < 2) return 0

    const current = filteredData.slice(-1)[0]?.users || 0
    const previous = filteredData.slice(-2)[0]?.users || 0

    if (previous === 0) return 0
    return ((current - previous) / previous) * 100
  }, [filteredData])

  const downloadCSV = () => {
    const csvData = filteredData.map(item => ({
      Date: item.date,
      Users: item.users,
    }))

    const csvHeaders = "Date,Users\n"
    const csvContent = csvHeaders + csvData.map(item =>
      `${item.Date},${item.Users}`
    ).join("\n")

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `registered_users_${format(dateRange[0].startDate, 'yyyyMMdd')}_${format(dateRange[0].endDate, 'yyyyMMdd')}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className=''>
      <Card>
        <Toaster />
        <CardHeader>
          <div className='flex justify-between items-start flex-col md:flex-row gap-4'>
            <div>
              <CardTitle>Customer Register Report</CardTitle>
              <CardDescription>
                {format(dateRange[0].startDate, "MMM dd, yyyy")} - {format(dateRange[0].endDate, "MMM dd, yyyy")}
              </CardDescription>
            </div>
            <div className="flex gap-3 relative">
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="text-muted-foreground"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Select Date Range
                </Button>
                {showCalendar && (
                  <div className="absolute right-0 mt-2 z-50 shadow-lg bg-white border rounded-lg">
                    <DateRange
                      editableDateInputs={true}
                      onChange={(item) => {
                        setDateRange([item.selection])
                        setShowCalendar(false)
                      }}
                      moveRangeOnFirstSelection={false}
                      ranges={dateRange}
                      maxDate={new Date()}
                      locale={enUS}
                    />
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                onClick={downloadCSV}
                disabled={isLoading || filteredData.length === 0}
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="mr-2 h-4 w-4" />
                )}
                Download Data
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-[250px]">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex items-center justify-center h-[250px]">
              <p className="text-muted-foreground">No user registrations found for the selected date range</p>
            </div>
          ) : (
            <ChartContainer config={chartConfig}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={filteredData}
                  margin={{
                    top: 20,
                    left: 12,
                    right: 12,
                    bottom: 12,
                  }}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(value) => format(new Date(value), "MMM dd")}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    allowDecimals={false}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        indicator="line"
                        labelFormatter={(value) => format(new Date(value), "MMM dd, yyyy")}
                        formatter={(value) => [`${value} users`, '']}
                      />
                    }
                  />
                  <Line
                    dataKey="users"
                    type="natural"
                    stroke={chartConfig.users.color}
                    strokeWidth={2}
                    dot={{
                      fill: chartConfig.users.color,
                    }}
                    activeDot={{
                      r: 6,
                    }}
                  >
                    <LabelList
                      position="top"
                      offset={12}
                      className="fill-foreground"
                      fontSize={12}
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          )}
        </CardContent>

        <CardFooter className="flex-col items-start gap-2 text-sm">
          <div className="flex gap-2 font-medium leading-none">
            {percentageChange >= 0 ? (
              <>
                Trending up by {Math.abs(percentageChange).toFixed(1)}%
                <TrendingUp className="h-4 w-4 text-green-500" />
              </>
            ) : (
              <>
                Trending down by {Math.abs(percentageChange).toFixed(1)}%
                <TrendingUp className="h-4 w-4 text-red-500 transform rotate-180" />
              </>
            )}
          </div>
          <div className="leading-none text-muted-foreground">
            Total days with registrations: {filteredData.length}
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}

export default RegisterReport
